import { useState, useEffect, useCallback } from 'react';
import useAuth from 'src/hooks/useAuth';
import { Key, Plus, Trash2, Copy, Check } from 'lucide-react';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { APIS_COMPONENT_DESIGN } from './ApisComponent.design';
import { Tooltip } from '@mui/material';
import ModalApis from './ModalApis';
import { useSnackbar } from 'notistack';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import axios from '../../../../utils/axios';
import MainWebhooks from '../Integracoes/MainWebooks';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
    { id: 'nome', label: 'Nome', className: 'flex-1' },
    { id: 'chave', label: 'Chave de API', className: 'w-96' },
    { id: 'actions', label: 'Acoes', className: 'w-24 text-right' },
];

export default function ApisComponent() {
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [openModal, setOpenModal] = useState(false);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [action, setAction] = useState('');
    const [listaApis, setListaApis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nome, setNome] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [tipo, setTipo] = useState('whatsapp');
    const [activeTab, setActiveTab] = useState('apis');

    const listarApis = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/v3/apis');
            setListaApis(response.data.data);
        } catch (error) {
            console.error('Erro ao listar APIs:', error);
            enqueueSnackbar('Erro ao listar chaves de API', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        listarApis();
    }, [listarApis]);

    const handleCopy = (id, key) => {
        navigator.clipboard.writeText(key);
        setCopiedId(id);
        enqueueSnackbar('Chave copiada!', { variant: 'success' });
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleCriar = async () => {
        if (!nome.trim()) return enqueueSnackbar('Nome e obrigatorio', { variant: 'warning' });

        try {
            const response = await axios.post('/v3/apis', { nome, type: tipo });
            const newKey = response.data.data;

            setListaApis([newKey, ...listaApis]);
            setOpenModal(false);
            setNome('');
            setTipo('whatsapp');
            enqueueSnackbar('Chave criada com sucesso!', { variant: 'success' });
        } catch (error) {
            console.error('Erro ao criar API:', error);
            enqueueSnackbar(error.mensagem || 'Erro ao criar chave de API', { variant: 'error' });
        }
    };

    const handleDeletar = async () => {
        try {
            await axios.delete(`/v3/apis/${selectedId}`);
            setListaApis(listaApis.filter(api => api._id !== selectedId));
            setOpenModalConfirmacao(false);
            setSelectedId('');
            enqueueSnackbar('Chave deletada com sucesso!', { variant: 'success' });
        } catch (error) {
            console.error('Erro ao deletar API:', error);
            enqueueSnackbar(error.mensagem || 'Erro ao deletar chave de API', { variant: 'error' });
        }
    };


    return (
        <div className={APIS_COMPONENT_DESIGN.page.container}>
            {/* Header */}
            <div className={APIS_COMPONENT_DESIGN.page.header.container}>
                <div>
                    <h1 className={APIS_COMPONENT_DESIGN.page.header.title}>APIs & Webhooks</h1>
                    <p className={APIS_COMPONENT_DESIGN.page.header.subtitle}>
                        Gerencie suas chaves de API e webhooks para integracoes externas.
                    </p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="bg-gray-100 rounded-xl p-1">
                    <TabsTrigger
                        value="apis"
                        className="rounded-lg px-6 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#7C3AED] data-[state=active]:shadow-sm"
                    >
                        Chaves de API
                    </TabsTrigger>
                    <TabsTrigger
                        value="webhooks"
                        className="rounded-lg px-6 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#7C3AED] data-[state=active]:shadow-sm"
                    >
                        Webhooks
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="apis" className="mt-6">
                    <ModalConfimacao
                        open={openModalConfirmacao}
                        onClose={() => setOpenModalConfirmacao(false)}
                        onConfirm={handleDeletar}
                        mensagem={'Deseja mesmo revogar esta chave de API? Esta acao e irreversivel e pode quebrar integracoes existentes.'}
                    />

                    <ModalApis
                        open={openModal}
                        action={action}
                        nome={nome}
                        tipo={tipo}
                        changeTipo={setTipo}
                        changeNome={(e) => setNome(e.target.value)}
                        onSubmit={handleCriar}
                        onClose={() => setOpenModal(false)}
                    />

                    {/* Action button for APIs */}
                    <div className="flex justify-end mb-4">
                        <button
                            onClick={() => {
                                setOpenModal(true);
                                setAction('Criar')
                                setNome('');
                                setTipo('whatsapp');
                            }}
                            className={APIS_COMPONENT_DESIGN.page.actionButton}
                        >
                            <Plus size={18} />
                            <span>Nova Chave</span>
                        </button>
                    </div>

                    {/* List */}
                    <div className="flex flex-col gap-4">
                        {listaApis.length === 0 && !loading ? (
                            <div className={APIS_COMPONENT_DESIGN.emptyState.container}>
                                <div className={APIS_COMPONENT_DESIGN.emptyState.iconBox}>
                                    <Key size={32} />
                                </div>
                                <h3 className={APIS_COMPONENT_DESIGN.emptyState.title}>Nenhuma chave criada</h3>
                                <p className={APIS_COMPONENT_DESIGN.emptyState.description}>
                                    Crie chaves de API para conectar sistemas externos.
                                </p>
                                <button
                                    onClick={() => {
                                        setOpenModal(true);
                                        setAction('Criar');
                                        setNome('');
                                    }}
                                    className={APIS_COMPONENT_DESIGN.emptyState.button}
                                >
                                    Criar primeira chave
                                </button>
                            </div>
                        ) : (
                            <GenericTable headLabels={TABLE_HEAD}>
                                {listaApis.map((row) => (
                                    <div
                                        key={row._id}
                                        className={GENERIC_TABLE_DESIGN.row.container}
                                    >
                                        {/* Nome */}
                                        <div className={GENERIC_TABLE_DESIGN.common.colMain}>
                                            <div className="flex items-center gap-3">
                                                <div className={GENERIC_TABLE_DESIGN.common.iconBox}>
                                                    <Key size={20} />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-gray-900">{row.nome}</h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Chave */}
                                        <div className="w-96 flex items-center gap-2">
                                            <code className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-600 font-mono flex-1 truncate">
                                                {row.token}
                                            </code>
                                            <Tooltip title="Copiar">
                                                <button
                                                    onClick={() => handleCopy(row._id, row.token)}
                                                    className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                                                >
                                                    {copiedId === row._id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                                                </button>
                                            </Tooltip>
                                        </div>

                                        {/* Actions */}
                                        <div className="w-24 flex justify-end gap-1">
                                            <Tooltip title="Revogar">
                                                <button
                                                    onClick={() => {
                                                        setSelectedId(row._id);
                                                        setOpenModalConfirmacao(true);
                                                    }}
                                                    className={GENERIC_TABLE_DESIGN.common.deleteButton}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </GenericTable>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="webhooks" className="mt-6">
                    <MainWebhooks />
                </TabsContent>
            </Tabs>
        </div>
    );
}
